/**
 * Role validation utilities for secure admin and user role checking
 * Replaces unsafe type casting with proper TypeScript validation
 */

// Define allowed user roles as a union type
export type UserRole = 'student' | 'tutor' | 'admin' | 'assistant' | 'accountant' | 'advertising';

// Type guard to validate if a value is a valid user role
export function isValidUserRole(role: any): role is UserRole {
  return typeof role === 'string' && ['student', 'tutor', 'admin', 'assistant', 'accountant', 'advertising'].includes(role);
}

// User interface with properly typed role
export interface SecureUser {
  id: string;
  email: string;
  role: UserRole;
  [key: string]: any;
}

// Type guard to validate if a user object has a valid role
export function isSecureUser(user: any): user is SecureUser {
  return (
    user &&
    typeof user === 'object' &&
    typeof user.id === 'string' &&
    typeof user.email === 'string' &&
    isValidUserRole(user.role)
  );
}

/**
 * Securely checks if a user is an admin
 * Replaces unsafe (user.role as any) === 'admin' patterns
 */
export function isAdmin(user: any): boolean {
  return isSecureUser(user) && user.role === 'admin';
}

/**
 * Securely checks if a user is a tutor
 */
export function isTutor(user: any): boolean {
  return isSecureUser(user) && user.role === 'tutor';
}

/**
 * Securely checks if a user is a student
 */
export function isStudent(user: any): boolean {
  return isSecureUser(user) && user.role === 'student';
}

/**
 * Securely checks if a user is an assistant
 */
export function isAssistant(user: any): boolean {
  return isSecureUser(user) && user.role === 'assistant';
}

/**
 * Securely checks if a user is an accountant
 */
export function isAccountant(user: any): boolean {
  return isSecureUser(user) && user.role === 'accountant';
}

/**
 * Securely checks if a user is in advertising team
 */
export function isAdvertising(user: any): boolean {
  return isSecureUser(user) && user.role === 'advertising';
}

/**
 * Checks if a user has admin or tutor privileges
 */
export function hasStaffPrivileges(user: any): boolean {
  return isAdmin(user) || isTutor(user);
}

/**
 * Checks if a user has admin or specific role privileges
 */
export function hasRoleAccess(user: any, allowedRoles: UserRole[]): boolean {
  if (!isSecureUser(user)) return false;
  return allowedRoles.includes(user.role);
}

/**
 * Safely gets the user role with validation
 */
export function getUserRole(user: any): UserRole | null {
  return isSecureUser(user) ? user.role : null;
}

/**
 * Error class for unauthorized access attempts
 */
export class UnauthorizedAccessError extends Error {
  constructor(requiredRole: UserRole, actualRole: string | null) {
    super(`Unauthorized access. Required role: ${requiredRole}, actual role: ${actualRole || 'unknown'}`);
    this.name = 'UnauthorizedAccessError';
  }
}

/**
 * Throws an error if user doesn't have required role
 */
export function requireRole(user: any, requiredRole: UserRole): asserts user is SecureUser {
  if (!isSecureUser(user)) {
    throw new UnauthorizedAccessError(requiredRole, null);
  }
  
  if (user.role !== requiredRole) {
    throw new UnauthorizedAccessError(requiredRole, user.role);
  }
}

/**
 * Throws an error if user doesn't have admin privileges
 */
export function requireAdmin(user: any): asserts user is SecureUser & { role: 'admin' } {
  requireRole(user, 'admin');
}